import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import ItemDescription from './Fields/ItemDescription';
import ItemID from './Fields/ItemID';
import ItemName from './Fields/ItemName';
import PricePerItem from './Fields/PricePerItem';
import BucketPriceRebate from './Fields/BucketPriceRebate';
import TurnaroundTime from './Fields/TurnaroundTime';

import { Button, ButtonGroup, ButtonWithLoader, CircularProgress, Drawer, DrawerBody, DrawerHeader, FlexRow, Heading, SubHeading } from '../../../../Styles';

import { updateErrorMessage, updateSuccessMessage } from '../../../../../actions/commonActions';
import { createInventory, fetchInventory, updateInventory, fetchBucketList } from '../../../../../actions/inventoryActionsV2';
import RadioGroupComp from './Fields/RadioGroup';
import { getDentalCorpBackend } from '../../../../../commons/backend/dentalCorpBackendProxy';
import store from '../../../../../store';

const ItemDetails = ({ open, itemId, onClose, onSubmit, ...props }) => {
	
	const performingItemUpdate = Boolean(itemId);
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [state, setState] = useState({});
	const [errors, setErrors] = useState({});
	const [initialBucketData, setInitialBucketData] = useState({});
	const [groupRebateEnabled, setGroupRebateEnabled] = useState(false);

	const fetchItem = async () => {
		let { fetchInventory } = props;

		setLoading(true);

		let product = await fetchInventory(itemId);
		let bucketData = {};

		const rebates = product?.rebates || {};
		
		(props.buckets || []).forEach((bucket) => {
			const bucketId = bucket.id;
			bucketData[`${bucketId}_price`] = product?.[bucketId] || '';
			bucketData[`${bucketId}_rebate`] = rebates[bucketId] || '';
		});

		let state = {
			...product,
			itemId: product?.supplier_product_id || '',
			itemName: product?.name || '',
			itemDescription: product?.description || '',
			price: product?.price || product?.price_list?.[1] || '',
			digitalOnly: 'digital_only' in product ? product?.digital_only : '',
			supportAddOns: 'support_add_ons' in product ? product?.support_add_ons : '',
			caseType: product?.caseTypeTags?.[0] || 'Any',
			toothType: product?.toothTypeTags?.[0] || 'Any',
			isAlloy: product?.is_alloy || false,
			turnaroundTime: product?.tat || '',
			...bucketData,
		};

		setInitialBucketData(bucketData);
		setState(state);
		setLoading(false);
	};

	const updateState = (values) => {
		setState((prevState) => ({
			...prevState,
			...values,
		}));
	};

	const updateError = (errors) => {
		setErrors((prevErrors) => ({
			...prevErrors,
			...errors,
		}));
	};

	const checkErrors = () => {
		return Object.keys(errors).some((key) => errors[key]);
	};

	const validateAllBucketPrices = () => {
		let hasErrors = false;
		const bucketErrors = {};

		(props.buckets || []).forEach((bucket) => {
			const bucketId = bucket.id;
			const priceKey = `${bucketId}_price`;
			const price = state[priceKey];
		
			if (price && price !== '' && price !== null && price !== undefined) {
				const numValue = parseFloat(price);
				if (isNaN(numValue) || numValue <= 0) {
					bucketErrors[priceKey] = 'Enter a valid price greater than 0.';
					hasErrors = true;
				}
			}
		});

		if (hasErrors) {
			setErrors((prevErrors) => ({
				...prevErrors,
				...bucketErrors,
			}));
		}

		return !hasErrors;
	};

	const isSaveDisabled = () => {
		return checkErrors();
	};

	const handleSubmit = async () => {
		let { updateErrorMessage, updateSuccessMessage, createInventory, updateInventory } = props;

		if (!validateAllBucketPrices()) {
			updateErrorMessage(true, 'Please fix bucket price errors before saving.');
			return;
		}

		if (checkErrors()) {
			updateErrorMessage(true, 'Clear errors before saving.');
			return;
		}

		let { itemId, itemName, itemDescription, price, caseType, toothType, isAlloy, turnaroundTime, ...rest } = state;
		let product = {
			supplier_product_id: itemId,
			name: itemName,
			description: itemDescription,
			caseTypeTags: (!caseType || caseType === "Any") ? null : [caseType],
			toothTypeTags: (!toothType || toothType === "Any") ? null : [toothType],
			is_alloy: isAlloy || false,
		};

		if (turnaroundTime && turnaroundTime !== '') {
			const parsedTat = parseInt(turnaroundTime, 10);
			if (!isNaN(parsedTat) && parsedTat > 1) {
				product.tat = parsedTat;
			} else {
				product.tat = null;
			}
		} else {
			product.tat = null;
		}

		if (price && price !== '' && price !== null && price !== undefined) {
			const parsedPrice = parseFloat(price);
			if (!isNaN(parsedPrice) && parsedPrice > 0) {
				product.price_list = { 1: parsedPrice };
			} else {
				product.price_list = null;
			}
		} else {
			product.price_list = null;
		}
		
		for(let bucket of props.buckets){
			const bucketId = bucket.id;
			const priceKey = `${bucketId}_price`;
			const rebateKey = `${bucketId}_rebate`;
			
			const hasValidPrice = rest[priceKey] && rest[priceKey] !== '' && rest[priceKey] !== null && rest[priceKey] !== undefined;
			let parsedPrice = null;
			if (hasValidPrice) {
				parsedPrice = parseFloat(rest[priceKey]);
				if (!isNaN(parsedPrice) && parsedPrice > 0) {
					product[bucketId] = parsedPrice;
				} else {
					product[bucketId] = null;
				}
			} else {
				product[bucketId] = null;
			}
			
			if (hasValidPrice && !isNaN(parsedPrice) && parsedPrice > 0 && props.rebateEnabled && groupRebateEnabled && rest[rebateKey] && rest[rebateKey] !== '' && rest[rebateKey] !== null && rest[rebateKey] !== undefined) {
				const rebateValue = parseFloat(rest[rebateKey]);
				if (!isNaN(rebateValue)) {
					product[`${bucketId}_rebate`] = rebateValue;
				} else {
					product[`${bucketId}_rebate`] = null;
				}
			} else {
				product[`${bucketId}_rebate`] = null;
			}
		}

		setSubmitting(true);

		let response = null;

		if (performingItemUpdate) {
			response = await updateInventory(itemId, product);
		} else {
			response = await createInventory(product);
		}

		setSubmitting(false);

		if (response) {
			updateSuccessMessage(true, `Item ${performingItemUpdate ? 'updated' : 'added'} successfully. Item Id: ${product?.supplier_product_id}`);
      
			if (onClose) {
        onSubmit();
				onClose();
			}
		}
	};

	const fetchLabRebateConfig = React.useCallback(async () => {
		const labId = props.supplier;
		
		if (!labId) {
			setGroupRebateEnabled(false);
			return;
		}
		
		const dentalCorpBackend = getDentalCorpBackend();
		try {
			const currentState = store.getState();
			const dispatch = store.dispatch;
			
			const response = await dentalCorpBackend.getLabRebateConfig(currentState, labId, dispatch);
			
			// Handle different response formats
			let rebateEnabled = false;
			if (response) {
				// Check if response has nested response property
				if (response.response) {
					rebateEnabled = response.response.rebate_enabled === true;
				} 
				// Check if response is the data directly
				else if (response.rebate_enabled !== undefined) {
					rebateEnabled = response.rebate_enabled === true;
				}
			}
			setGroupRebateEnabled(rebateEnabled);
		} catch (error) {
			// Default to disabled if fetch fails
			setGroupRebateEnabled(false);
		}
	}, [props.supplier]);

	useEffect(() => {
		if (open) {
			if (itemId) {
				fetchItem();
			}
			if (props.supplier) {
				fetchLabRebateConfig();
			} else {
				setGroupRebateEnabled(false);
			}
		}
		else {
			setState({});
			setErrors({});
			setInitialBucketData({});
			setGroupRebateEnabled(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, itemId]);

	// Fetch lab rebate config when modal opens
	useEffect(() => {
		if (open) {
			if (props.supplier) {
				fetchLabRebateConfig();
			} else {
				setGroupRebateEnabled(false);
			}
		}
	}, [open, props.supplier, fetchLabRebateConfig]);
	
	return (
		<Drawer
			open={open}
			anchor='right'
			onClose={(e) => {
				if (onClose) {
					onClose();
				}
			}}
		>
			<DrawerHeader>
				{`${performingItemUpdate ? 'Update' : 'Add'} Item`}

				{!loading && (
					<ButtonGroup alignRight>
						<Button
							outline
							disabled={submitting}
							onClick={(e) => {
								if (onClose) {
									onClose();
								}
							}}
						>
							Cancel
						</Button>

						<ButtonWithLoader
							loading={submitting}
							disabled={isSaveDisabled()}
							onClick={(e) => handleSubmit()}
						>
							Save
						</ButtonWithLoader>
					</ButtonGroup>
				)}
			</DrawerHeader>

			<DrawerBody>
				{loading ? (
					<CircularProgress showSpinner={loading} />
				) : (
					<>
						<Heading>Item Details</Heading>

						<FlexRow>
							<ItemID
								required={true}
								value={state?.itemId}
								error={errors?.itemId}
								disabled={performingItemUpdate}
								onChange={(value) => {
									updateState({ itemId: value });
								}}
								onError={(error) => {
									updateError({ itemId: error });
								}}
							/>
						</FlexRow>

						<FlexRow>
							<ItemName
								required={true}
								value={state?.itemName}
								error={errors?.itemName}
								onChange={(value) => {
									updateState({ itemName: value });
								}}
								onError={(error) => {
									updateError({ itemName: error });
								}}
							/>
						</FlexRow>

						<FlexRow>
							<ItemDescription
								required={true}
								value={state?.itemDescription}
								error={errors?.itemDescription}
								onChange={(value) => {
									updateState({ itemDescription: value });
								}}
								onError={(error) => {
									updateError({ itemDescription: error });
								}}
							/>
						</FlexRow>
						
						<FlexRow>
							<RadioGroupComp
								label="Is this an alloy SKU with unit-based pricing (per gram)?"
								options={[
									{ name: "Yes", value: true },
									{ name: "No", value: false },
								]}
								value={state?.isAlloy}
								error={errors?.isAlloy}
								onChange={(value) => updateState({ isAlloy: value === 'true' })}
							/>
						</FlexRow>
						<FlexRow>
							<TurnaroundTime
								label="Turnaround Time"
								required={false}
								value={state?.turnaroundTime}
								error={errors?.turnaroundTime}
								disabled={submitting}
								onChange={(value) => {
									updateState({ turnaroundTime: value });
								}}
								onError={(error) => {
									updateError({ turnaroundTime: error });
								}}
							/>
						</FlexRow>

						<FlexRow>
							<PricePerItem
								label={"Base Price" + (state?.isAlloy ? " (per gram)" : "")}
								required={false}
								value={state?.price}
								error={errors?.price}
								disabled={submitting}
								onChange={(value) => {
									updateState({ price: value });
								}}
								onError={(error) => {
									updateError({ price: error });
								}}
							/>
						</FlexRow>

					{(props?.buckets || []).length > 0 && (
						<>
							<SubHeading>
								Bucket Pricing & Rebates
							</SubHeading>

							{(props.buckets || []).map((bucket) => {
								const bucketId = bucket?.id;
								const bucketName = bucket?.alias;
								const priceKey = `${bucketId}_price`;
								const rebateKey = `${bucketId}_rebate`;
								
								return (
									<BucketPriceRebate
										key={bucketId}
										bucketId={bucketId}
										bucketName={bucketName}
										price={state?.[priceKey]}
										rebate={state?.[rebateKey]}
										priceError={errors?.[priceKey]}
										rebateError={errors?.[rebateKey]}
										onPriceChange={(value) => {
											updateState({ [priceKey]: value });
										}}
										onRebateChange={(value) => {
											updateState({ [rebateKey]: value });
										}}
										onPriceError={(error) => {
											updateError({ [priceKey]: error });
										}}
										onRebateError={(error) => {
											updateError({ [rebateKey]: error });
										}}
										rebateEnabled={props.rebateEnabled !== false && groupRebateEnabled}
										disabled={submitting}
										isAlloy={state?.isAlloy}
									/>
								);
							})}
						</>
					)}


						<SubHeading>Case Type</SubHeading>

						<FlexRow>
							<RadioGroupComp
								value={state?.caseType}
								error={errors?.caseType}
								onChange={(value) => {
									updateState({ caseType: value });
								}}
								onError={(error) => {
									updateError({ caseType: error });
								}}
								options={[
									{ name: "Any", value: "Any" },
									{ name: "Digital", value: "DIGITAL" },
									{ name: "Analog", value: "ANALOG" }
								]}
							/>
							{/* <SupportAddOns
								value={state?.supportAddOns}
								error={errors?.supportAddOns}
								onChange={(value) => {
									updateState({ supportAddOns: value });
								}}
								onError={(error) => {
									updateError({ supportAddOns: error });
								}}
							/> */}
						</FlexRow>
						<SubHeading>Tooth Type</SubHeading>
						<FlexRow>
							<RadioGroupComp
								value={state?.toothType}
								error={errors?.toothType}
								onChange={(value) => {
									updateState({ toothType: value });
								}}
								onError={(error) => {
									updateError({ toothType: error });
								}}
								options={[
									{ name: "Any", value: "Any" },
									{ name: "Posterior", value: "POSTERIOR", description: "Relating to or situated at the back or rear of the body or structure." },
									{ name: "Anterior", value: "ANTERIOR", description: "Relating to or situated at the front of the body or structure." }
								]}
							/>
						</FlexRow>
					</>
				)}
			</DrawerBody>
		</Drawer>
	);
};

const mapStateToProps = (state) => {
	const supplierSellerPortalConfig = state.supplier.sellerPortalConfig || {};
	const userSellerPortalConfig = state.session.sellerPortalConfig || {};
	
	const rebateEnabled = !(supplierSellerPortalConfig.disable_rebate || userSellerPortalConfig.disable_rebate);
	
	return {
		buckets: state.inventory.buckets || [],
		bucketOrder: state.inventory.bucketOrder || [],
		rebateEnabled: rebateEnabled,
		session: state.session || {},
		supplier: state?.supplier?.supplier || ''
	};
};

const mapDispatch = { updateSuccessMessage, updateErrorMessage, fetchInventory, createInventory, updateInventory, fetchBucketList };

export default withRouter(connect(mapStateToProps, mapDispatch)(ItemDetails));
